/**
 * Collection product sort helpers (API: sort_by / sort_order on collection + GET storefront).
 * @see ordering.md
 */

export const SORT_SELECT_OPTIONS = [
    { id: 1, name: 'A - Z' },
    { id: 2, name: 'Z - A' },
    { id: 3, name: 'Mayor precio - Menor precio' },
    { id: 4, name: 'Menor precio - Mayor precio' },
    { id: 5, name: 'Más reciente - Más antiguo' },
    { id: 6, name: 'Más antiguo - Más reciente' },
];

/** All automatic sort modes available in subcategory accordion UI */
export const SUBCATEGORY_SORT_OPTIONS = [...SORT_SELECT_OPTIONS];

const SORT_STORAGE_PREFIX = 'wa10x-collection-sort';

function normalizeSortBy(value) {
    if (value == null || value === '') return 'manual';
    const v = String(value).toLowerCase();
    if (v === 'manual' || v === 'default') return 'manual';
    if (v === 'name' || v === 'nombre') return 'name';
    if (v === 'price' || v === 'precio') return 'price';
    if (v === 'created_at' || v === 'createdat' || v === 'date' || v === 'fecha') return 'created_at';
    return v;
}

function normalizeSortOrder(value) {
    const v = String(value ?? 'asc').toLowerCase();
    return v === 'desc' ? 'desc' : 'asc';
}

function readSortByFromCollection(collection) {
    const nested = collection.product_order || collection.productOrder;
    return (
        collection.sort_by ??
        collection.sortBy ??
        collection.products_sort_by ??
        collection.product_sort_by ??
        nested?.sort_by ??
        nested?.sortBy
    );
}

function readSortOrderFromCollection(collection) {
    const nested = collection.product_order || collection.productOrder;
    return (
        collection.sort_order ??
        collection.sortOrder ??
        collection.products_sort_order ??
        collection.product_sort_order ??
        nested?.sort_order ??
        nested?.sortOrder
    );
}

/** Normalize GET/PUT collection body so sort fields are always on the root object. */
export function normalizeCollectionFromApi(collection) {
    if (!collection || typeof collection !== 'object') {
        return collection;
    }
    const { sort_by, sort_order } = getCollectionSortFields(collection);
    return {
        ...collection,
        sort_by,
        sort_order,
        sortBy: sort_by,
        sortOrder: sort_order,
    };
}

export function collectionHasSortFromApi(collection) {
    if (!collection || typeof collection !== 'object') return false;
    return [readSortByFromCollection(collection), readSortOrderFromCollection(collection)].some(
        (v) => v != null && v !== ''
    );
}

export function getCollectionSortStorageKey(storeId, collectionId) {
    return `${SORT_STORAGE_PREFIX}:${storeId}:${collectionId}`;
}

export function persistCollectionSortPreference(storeId, collectionId, sort_by, sort_order) {
    if (typeof window === 'undefined' || !storeId || !collectionId) return;
    const normalizedBy = normalizeSortBy(sort_by);
    const normalizedOrder = normalizeSortOrder(sort_order);
    try {
        window.localStorage.setItem(
            getCollectionSortStorageKey(storeId, collectionId),
            JSON.stringify({
                sort_by: normalizedBy,
                sort_order: normalizedOrder,
            })
        );
    } catch {
        /* ignore quota / private mode */
    }
}

export function loadCollectionSortPreference(storeId, collectionId) {
    if (typeof window === 'undefined' || !storeId || !collectionId) return null;
    try {
        const raw = window.localStorage.getItem(getCollectionSortStorageKey(storeId, collectionId));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return {
            sort_by: normalizeSortBy(parsed.sort_by),
            sort_order: normalizeSortOrder(parsed.sort_order),
        };
    } catch {
        return null;
    }
}

/**
 * Merge API collection with cached sort when the API does not return sort_by/sort_order.
 */
export function enrichCollectionWithSortPreference(collection, storeId) {
    if (!collection || typeof collection !== 'object') {
        return collection;
    }
    const normalized = normalizeCollectionFromApi(collection);
    const fromApi = getCollectionSortFields(normalized);
    const hasApiSort = collectionHasSortFromApi(normalized);

    if (hasApiSort && fromApi.sort_by !== 'manual') {
        if (storeId && normalized.id) {
            persistCollectionSortPreference(storeId, normalized.id, fromApi.sort_by, fromApi.sort_order);
        }
        return normalizeCollectionFromApi({
            ...normalized,
            sort_by: fromApi.sort_by,
            sort_order: fromApi.sort_order,
        });
    }

    const cached =
        storeId && normalized.id ? loadCollectionSortPreference(storeId, normalized.id) : null;
    if (cached?.sort_by && cached.sort_by !== 'manual') {
        if (process.env.NODE_ENV === 'development') {
            console.warn(
                `[collection sort] GET collection ${normalized.id} sin sort_by; usando cache local. ` +
                    'Verifica dev-api y ordering.md'
            );
        }
        persistCollectionSortPreference(storeId, normalized.id, cached.sort_by, cached.sort_order);
        return normalizeCollectionFromApi({
            ...normalized,
            sort_by: cached.sort_by,
            sort_order: cached.sort_order,
        });
    }

    return normalizeCollectionFromApi(normalized);
}

export function getCollectionSortFields(collection) {
    if (!collection || typeof collection !== 'object') {
        return { sort_by: 'manual', sort_order: 'asc' };
    }
    const sort_by = normalizeSortBy(readSortByFromCollection(collection));
    const sort_order = normalizeSortOrder(readSortOrderFromCollection(collection));
    return { sort_by, sort_order };
}

export function getSortSelectIdFromCollection(collection) {
    const { sort_by, sort_order } = getCollectionSortFields(collection);
    if (sort_by === 'manual') return null;
    if (sort_by === 'name') return sort_order === 'desc' ? 2 : 1;
    if (sort_by === 'price') return sort_order === 'desc' ? 3 : 4;
    if (sort_by === 'created_at') return sort_order === 'desc' ? 5 : 6;
    return null;
}

export function getSubcategorySortSelectIdFromCollection(collection) {
    return getSortSelectIdFromCollection(collection);
}

export function isSubcategorySortSelectId(selectId) {
    return SUBCATEGORY_SORT_OPTIONS.some((o) => o.id === Number(selectId));
}

export function getSortConfigFromSelectId(selectId) {
    switch (Number(selectId)) {
        case 1:
            return { sort_by: 'name', sort_order: 'asc' };
        case 2:
            return { sort_by: 'name', sort_order: 'desc' };
        case 3:
            return { sort_by: 'price', sort_order: 'desc' };
        case 4:
            return { sort_by: 'price', sort_order: 'asc' };
        case 5:
            return { sort_by: 'created_at', sort_order: 'desc' };
        case 6:
            return { sort_by: 'created_at', sort_order: 'asc' };
        default:
            return { sort_by: 'manual', sort_order: 'asc' };
    }
}

export function sortProductsBySelectId(products, selectId) {
    const list = [...products];
    switch (Number(selectId)) {
        case 1:
            return list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        case 2:
            return list.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
        case 3:
            return list.sort((a, b) => (Number(b.price) || 0) - (Number(a.price) || 0));
        case 4:
            return list.sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0));
        case 5:
            return list.sort(
                (a, b) =>
                    new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
            );
        case 6:
            return list.sort(
                (a, b) =>
                    new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()
            );
        default:
            return list;
    }
}

/** Re-apply the collection's saved sort (no-op when sort is manual / default). */
export function applySavedSortToProducts(products, collectionData, sortOverrides = {}) {
    const merged = { ...collectionData, ...sortOverrides };
    const selectId = getSortSelectIdFromCollection(merged);
    if (selectId == null) return [...products];
    return sortProductsBySelectId(products, selectId);
}

/** Add a product: prepend for manual order, otherwise re-sort the full list. */
export function addProductWithCollectionSort(products, newProduct, collectionData) {
    const pid = String(newProduct.id ?? newProduct.product_id ?? newProduct);
    const withoutNew = (products || []).filter(
        (p) => String(p.id ?? p.product_id ?? p) !== pid
    );
    const { sort_by } = getCollectionSortFields(collectionData);
    const withNew = [{ ...newProduct, id: newProduct.id ?? newProduct.product_id ?? newProduct }, ...withoutNew];
    if (sort_by === 'manual') return withNew;
    return applySavedSortToProducts(withNew, collectionData);
}

export function buildCollectionUpdatePayload(collectionData, productIds = [], overrides = {}) {
    const merged = { ...collectionData, ...overrides };
    const { sort_by, sort_order } = getCollectionSortFields(merged);
    return {
        id: collectionData.id,
        name: collectionData.name,
        description: collectionData.description ?? '',
        public: Number(collectionData.public) ? 1 : 0,
        sort_by,
        sort_order,
        sortBy: sort_by,
        sortOrder: sort_order,
        products: productIds.map(String),
    };
}

/**
 * Puts product first in each collection (storefront + CMS manual order).
 */
export async function prependProductToCollections(storeId, productId, collectionIds, {
    collectionsxIdService,
    updateCollectionsService,
}) {
    if (!storeId || !productId || !collectionIds?.length || !updateCollectionsService) return;

    const uniqueIds = [...new Set(collectionIds.map(String).filter(Boolean))];

    await Promise.all(
        uniqueIds.map(async (colId) => {
            const { data } = await collectionsxIdService(storeId, colId);
            const existing = data.products || [];
            const newProduct =
                existing.find((p) => String(p.id ?? p.product_id ?? p) === String(productId)) || {
                    id: productId,
                };
            const ordered = addProductWithCollectionSort(existing, newProduct, data);
            await updateCollectionsService(
                storeId,
                buildCollectionUpdatePayload(
                    data,
                    ordered.map((p) => String(p.id ?? p.product_id ?? p))
                )
            );
        })
    );
}
